import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import axios from 'axios';

import { getApiErrorCode } from './utils/apiError';

function extractErrorMessageFromResponse(error) {
  const v1ErrorMessage = error.response?.data?.error?.text;
  if (typeof v1ErrorMessage === 'string') return v1ErrorMessage;

  if (error.response?.status === 500) return null;

  const v3ErrorMessage =
    (typeof error.response?.data?.message?.[0]?.message === 'string' &&
      error.response?.data?.message?.[0]?.message) ||
    (typeof error.response?.data?.message === 'string' && error.response?.data?.message);
  if (typeof v3ErrorMessage === 'string') return v3ErrorMessage;

  return null;
}

function isSilenced(silenceErrors, error) {
  if (Array.isArray(silenceErrors)) {
    const code = getApiErrorCode(error);
    return code !== undefined && silenceErrors.includes(code);
  }
  return Boolean(silenceErrors);
}

export default new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (axios.isCancel(error)) return;

      // Best-effort queries (optional presets, prefetches) opt out entirely: a failure there
      // has a silent fallback, so an error toast would only confuse. A list of API error
      // codes silences only those, as for mutations below.
      if (isSilenced(query.meta?.silenceErrors, error)) return;

      const apiErrorMessage = extractErrorMessageFromResponse(error);
      if (apiErrorMessage) {
        enqueueSnackbar(apiErrorMessage, { variant: 'error' });
        return;
      }

      if (query.meta?.errorMessage) {
        enqueueSnackbar(query.meta.errorMessage, { variant: 'error' });
      }
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (data, variables, context, mutation) => {
      if (!mutation.meta?.successMessage) return;

      if (typeof mutation.meta?.successMessage === 'string') {
        enqueueSnackbar(mutation.meta?.successMessage, { variant: 'success' });
        return;
      }

      if (!mutation.meta.successMessage.field) return;

      const successMessage = variables[mutation.meta.successMessage.field]
        ? mutation.meta.successMessage.update
        : mutation.meta.successMessage.create;
      if (successMessage) {
        enqueueSnackbar(successMessage, { variant: 'success' });
      }
    },
    onError: (error, variables, context, mutation) => {
      if (axios.isCancel(error)) return;

      // Pages that surface failures inline opt out, mirroring the query cache above: `true`
      // silences every failure, a list silences only the API error codes it names.
      if (isSilenced(mutation.meta?.silenceErrors, error)) return;

      if (error.response?.status === 403) {
        enqueueSnackbar('User not authorized to perform requested action', { variant: 'error' });
        return;
      }

      const apiErrorMessage = extractErrorMessageFromResponse(error);
      if (apiErrorMessage) {
        enqueueSnackbar(apiErrorMessage, { variant: 'error' });
        return;
      }

      if (mutation.meta?.errorMessage) {
        if (typeof mutation.meta.errorMessage === 'string') {
          enqueueSnackbar(mutation.meta.errorMessage, { variant: 'error' });
          return;
        }

        const errorMessage = variables[mutation.meta.errorMessage.field]
          ? mutation.meta.errorMessage.update
          : mutation.meta.errorMessage.create;
        if (errorMessage) {
          enqueueSnackbar(errorMessage, { variant: 'error' });
          return;
        }
      }

      enqueueSnackbar(error.message, { variant: 'error' });
    },
  }),
});
